import React, { useState, useMemo, useCallback, Suspense, lazy } from 'react';
import './App.css';
import VideoCard from './components/VideoCard';
import VideoModal from './components/VideoModal';
import { videos } from './data/videos';
import PhotoCard from './components/PhotoCard';
import PhotoModal from './components/PhotoModal';
import { photos } from './data/photos';

// Lazy load below-the-fold components
const Team = lazy(() => import('./components/Team'));
const Clients = lazy(() => import('./components/Clients'));
const About = lazy(() => import('./components/About'));
const Services = lazy(() => import('./components/Services'));

function App() {
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoUrl, setModalVideoUrl] = useState(null);
  const [activeView, setActiveView] = useState('videos'); // 'videos' or 'photos'
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [modalPhotoUrl, setModalPhotoUrl] = useState(null);
  const [modalPhotoTitle, setModalPhotoTitle] = useState(null);

  const videosToShow = useMemo(() => 
    showAllVideos ? videos : videos.slice(0, 12), 
    [showAllVideos]
  );
  
  const photosToShow = useMemo(() => 
    showAllPhotos ? photos : photos.slice(0, 12), 
    [showAllPhotos]
  );

  const scrollToSection = useCallback((e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleVideoClick = useCallback((videoUrl) => {
    setModalVideoUrl(videoUrl);
    setModalOpen(true);
  }, []);

  const handlePhotoClick = useCallback((photoUrl, photoTitle) => {
    setModalPhotoUrl(photoUrl);
    setModalPhotoTitle(photoTitle);
    setPhotoModalOpen(true);
  }, []);

  const handleToggleVideos = useCallback(() => {
    setActiveView('videos');
    setShowAllVideos(false);
  }, []);

  const handleTogglePhotos = useCallback(() => {
    setActiveView('photos');
    setShowAllPhotos(false);
  }, []);

  return (
    <div className="App">
      <header className="header">
        <nav className="nav">
          <div className="logo" style={{cursor: 'pointer'}} onClick={(e) => scrollToSection(e, 'home')}>
            <img src="/lablogo.jpg" alt="Film-Lab Logo" className="navbar-logo" loading="eager" />
          </div>
          <ul className="nav-links">
            <li><a href="#home" onClick={(e) => scrollToSection(e, 'home')}>HOME</a></li>
            <li><a href="#videos" onClick={(e) => scrollToSection(e, 'videos')}>VIDEOS</a></li>
            <li><a href="#about" onClick={(e) => scrollToSection(e, 'about')}>ABOUT US</a></li>
            <li><a href="#team" onClick={(e) => scrollToSection(e, 'team')}>OUR TEAM</a></li>
            <li><a href="#clients" onClick={(e) => scrollToSection(e, 'clients')}>OUR CLIENTS</a></li>
            <li>
              <a href="mailto:info@filmlabz.com">CONTACT US</a>
            </li>
          </ul>
        </nav>
      </header>

      <main>
        <section id="home" className="hero">
          <video
            className="hero-bg-video"
            src="/For Web.mp4"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          />
          <div className="hero-content">
            <h1>Welcome to the Home of New Ideas</h1>
            <button className="cta-button" onClick={(e) => scrollToSection(e, 'videos')}>WATCH VIDEOS</button>
          </div>
        </section>

        <section id="videos" className="videos">
          <h2>Gallery</h2>
          
          {/* Toggle Buttons */}
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${activeView === 'videos' ? 'active' : ''}`}
              onClick={handleToggleVideos}
            >
              VIDEOS
            </button>
            <button 
              className={`toggle-btn ${activeView === 'photos' ? 'active' : ''}`}
              onClick={handleTogglePhotos}
            >
              PHOTOS
            </button>
          </div>

          {/* Videos View */}
          {activeView === 'videos' && (
            <>
              <div className="video-grid">
                {videosToShow.map(video => (
                  <VideoCard
                    key={video.id}
                    title={video.title}
                    thumbnail={video.thumbnail}
                    onClick={() => handleVideoClick(video.videoUrl)}
                  />
                ))}
              </div>
              {!showAllVideos && videos.length > 12 && (
                <div className="view-more-container">
                  <button className="view-more-btn" onClick={() => setShowAllVideos(true)}>
                    View More
                  </button>
                </div>
              )}
            </>
          )}

          {/* Photos View */}
          {activeView === 'photos' && (
            <>
              <div className="photo-grid">
                {photosToShow.map(photo => (
                  <PhotoCard
                    key={photo.id}
                    title={photo.title}
                    image={photo.image}
                    onClick={() => handlePhotoClick(photo.image, photo.title)}
                  />
                ))}
              </div>
              {!showAllPhotos && photos.length > 12 && (
                <div className="view-more-container">
                  <button className="view-more-btn" onClick={() => setShowAllPhotos(true)}>
                    View More
                  </button>
                </div>
              )}
            </>
          )}

          {/* Modals */}
          <VideoModal videoUrl={modalVideoUrl} open={modalOpen} onClose={() => setModalOpen(false)} />
          <PhotoModal 
            photoUrl={modalPhotoUrl} 
            photoTitle={modalPhotoTitle}
            open={photoModalOpen} 
            onClose={() => setPhotoModalOpen(false)} 
          />
        </section>

        <Suspense fallback={
          <div style={{padding: '4rem 2rem', textAlign: 'center', background: '#f5f5f5', minHeight: '200px'}}>
            <p>Loading...</p>
          </div>
        }>
          <About />
          <Services />
          <Team />
          <Clients />
        </Suspense>
      </main>

      <footer className="footer">
        <p>©Copyright 2025 Film-Lab. All Rights Reserved.</p>
      </footer>
    </div>
  );
}

export default App;